"use client";

import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import VideoPlayer from "@/components/VideoPlayer";
import ContentChat from "@/components/ContentChat";
import ContentCard from "@/components/ContentCard";
import { Shield, Eye, Clock, Lock, DollarSign, ThumbsUp, Share2, Loader2, X } from "lucide-react";

export default function WatchPage() {
  const params = useParams();
  const contentId = params.id as string;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [subbing, setSubbing] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);

  const load = () => {
    fetch(`/api/content/${contentId}`)
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [contentId]);

  const handleUnlock = async () => {
    setUnlocking(true);
    const res = await fetch(`/api/content/${contentId}/unlock`, { method: "POST" });
    const result = await res.json();
    setUnlocking(false);
    if (res.ok) load();
    else alert(result.error || "Failed to post bail");
  };

  const handleSubscribe = async () => {
    if (!data?.creator?.id) return;
    setSubbing(true);
    const res = await fetch(`/api/creator/${data.creator.id}/subscribe`, { method: "POST" });
    const result = await res.json();
    setSubbing(false);
    if (res.ok) load();
    else alert(result.error || "Visitation failed");
  };

  const handleTip = async () => {
    if (!data?.creator?.id || tipAmount <= 0) return;
    const res = await fetch("/api/credits/tip", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ creatorId: data.creator.id, amount: tipAmount }),
    });
    if (res.ok) { setTipOpen(false); setTipAmount(0); }
    else { const r = await res.json().catch(() => ({})); alert(r.error || "Drop failed"); }
  };

  if (loading) return <main className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-6 h-6 text-neutral-500 animate-spin" /></main>;

  if (!data || data.error) return <main className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="text-center"><p className="text-neutral-400 mb-4">Content not found</p><Link href="/browse" className="text-red-500 text-sm font-bold">Enter The Yard</Link></div></main>;

  const { hasAccess, isSubscribed, creator, related } = data;
  const needsSub = data.access === "SUBSCRIBERS" && !hasAccess;
  const needsPPV = data.access === "PPV" && !hasAccess;

  let tags: string[] = [];
  try { tags = typeof data.tags === "string" ? JSON.parse(data.tags) : data.tags || []; } catch {}

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <div className="flex flex-col xl:flex-row">

        <div className="flex-1 min-w-0">

          {/* PLAYER / PAYWALL */}
          <div className="w-full aspect-video bg-black relative">
            {hasAccess && data.videoUrl ? (
              <VideoPlayer src={data.videoUrl} poster={data.thumbnailUrl} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-neutral-950">
                {data.thumbnailUrl && <img src={data.thumbnailUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 blur-xl" />}
                <div className="relative z-10 max-w-md mx-4 text-center">
                  <div className="bg-[#0a0a0a]/90 border border-neutral-800 rounded-xl p-8 backdrop-blur-md">
                    <Lock className="w-10 h-10 text-red-500 mx-auto mb-4" />
                    {needsSub && (
                      <>
                        <h2 className="text-xl font-black uppercase tracking-tight mb-2">Visitation Only</h2>
                        <p className="text-neutral-400 text-sm mb-6">Get visitation with {creator.username} to unlock this and all their contraband.</p>
                        <button onClick={handleSubscribe} disabled={subbing} className="w-full bg-red-600 hover:bg-red-500 text-white py-3 rounded-lg font-bold uppercase text-sm transition disabled:opacity-50 mb-3">
                          {subbing ? "Processing..." : `Get Visitation -- ${creator.subscriptionPrice.toLocaleString()} Commissary / month`}
                        </button>
                      </>
                    )}
                    {needsPPV && (
                      <>
                        <h2 className="text-xl font-black uppercase tracking-tight mb-2">Post Bail</h2>
                        <p className="text-neutral-400 text-sm mb-6">One-time unlock. Watch anytime after posting bail.</p>
                        <button onClick={handleUnlock} disabled={unlocking} className="w-full bg-red-600 hover:bg-red-500 text-white py-3 rounded-lg font-bold uppercase text-sm transition disabled:opacity-50 mb-3">
                          {unlocking ? "Processing..." : `Post Bail -- ${data.ppvPrice?.toLocaleString()} Commissary`}
                        </button>
                        {!isSubscribed && (
                          <button onClick={handleSubscribe} disabled={subbing} className="w-full bg-neutral-900 border border-neutral-700 text-neutral-300 py-2.5 rounded-lg font-bold uppercase text-xs transition disabled:opacity-50">
                            {subbing ? "..." : `Or get visitation ${creator.subscriptionPrice.toLocaleString()} Commissary/mo`}
                          </button>
                        )}
                      </>
                    )}
                    {!needsSub && !needsPPV && !hasAccess && (
                      <>
                        <h2 className="text-xl font-black uppercase tracking-tight mb-2">Check In Required</h2>
                        <p className="text-neutral-400 text-sm mb-6">Sign in to view this contraband.</p>
                        <Link href="/auth/login" className="block w-full bg-red-600 text-white py-3 rounded-lg font-bold uppercase text-sm text-center">Sign In</Link>
                      </>
                    )}
                    <Link href="/subscribe" className="block mt-3 text-[11px] text-neutral-500 hover:text-green-400 transition">Need commissary? Hit the window</Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* INFO */}
          <div className="px-4 md:px-6 py-5">
            <h1 className="text-lg md:text-xl font-bold text-white leading-tight mb-3">{data.title}</h1>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-neutral-800/40">
              <div className="flex items-center gap-4 text-xs text-neutral-500 font-bold">
                <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {data.views.toLocaleString()}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {new Date(data.createdAt).toLocaleDateString()}</span>
                <span className="bg-neutral-900 px-2 py-0.5 rounded text-[10px] uppercase border border-neutral-800">{data.category}</span>
                {data.access !== "FREE" && (
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-black ${data.access === "PPV" ? "bg-yellow-900/20 text-yellow-500 border border-yellow-900/30" : "bg-red-900/20 text-red-400 border border-red-900/30"}`}>
                    {data.access === "PPV" ? `BAIL ${data.ppvPrice} CR` : "VISITATION"}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button className="flex items-center gap-1.5 h-8 px-4 rounded-md bg-neutral-900 border border-neutral-800 text-neutral-300 hover:text-white transition text-[11px] font-bold"><ThumbsUp className="w-3.5 h-3.5" /> {data.likes}</button>
                <button className="flex items-center gap-1.5 h-8 px-4 rounded-md bg-neutral-900 border border-neutral-800 text-neutral-300 hover:text-white transition text-[11px] font-bold"><Share2 className="w-3.5 h-3.5" /> Share</button>
              </div>
            </div>

            {/* INMATE INFO */}
            <div className="py-4 flex items-center justify-between border-b border-neutral-800/40">
              <div className="flex items-center gap-3">
                <Link href={`/creator/${creator.id}`}>
                  {creator.avatarUrl ? <img src={creator.avatarUrl} alt="" className="w-11 h-11 rounded-full object-cover border-2 border-neutral-700 hover:border-red-600/50 transition" /> : <div className="w-11 h-11 rounded-full bg-neutral-800 flex items-center justify-center font-bold">{creator.username[0]}</div>}
                </Link>
                <div>
                  <Link href={`/creator/${creator.id}`} className="font-bold text-white hover:text-red-500 transition text-sm flex items-center gap-1">
                    {creator.username}
                    {creator.isVerified && <Shield className="w-3.5 h-3.5 text-blue-500" />}
                  </Link>
                  <div className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider">{creator._count.subscribers} pen pals</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isSubscribed ? (
                  <span className="h-8 px-4 flex items-center gap-1.5 rounded-md bg-green-900/20 border border-green-800 text-green-400 text-[11px] font-bold uppercase"><Shield className="w-3.5 h-3.5" /> Visiting</span>
                ) : (
                  <button onClick={handleSubscribe} disabled={subbing} className="h-8 px-4 flex items-center rounded-md bg-red-600 hover:bg-red-500 text-white text-[11px] font-bold uppercase transition disabled:opacity-50">Get Visitation {creator.subscriptionPrice} CR</button>
                )}
                <button onClick={() => setTipOpen(!tipOpen)} className="h-8 px-3 flex items-center gap-1 rounded-md bg-neutral-900 border border-neutral-800 text-neutral-300 hover:text-green-400 hover:border-green-800/50 transition text-[11px] font-bold"><DollarSign className="w-3.5 h-3.5 text-green-500" /> Drop</button>
              </div>
            </div>

            {/* TIP */}
            {tipOpen && (
              <div className="py-3 border-b border-neutral-800/40">
                <div className="flex items-center gap-2 mb-2">
                  {[50, 100, 250, 500, 1000].map((amt) => (
                    <button key={amt} onClick={() => setTipAmount(amt)} className={`px-3 py-1.5 rounded-md text-xs font-bold transition border ${tipAmount === amt ? "border-green-600 bg-green-900/20 text-green-400" : "border-neutral-800 text-neutral-400"}`}>{amt}</button>
                  ))}
                  <button onClick={() => setTipOpen(false)} className="ml-auto text-neutral-500 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex gap-2">
                  <input type="number" value={tipAmount || ""} onChange={(e) => setTipAmount(parseInt(e.target.value) || 0)} placeholder="Custom" className="flex-1 h-9 bg-neutral-900 border border-neutral-800 rounded-md px-3 text-sm text-white focus:border-green-600 focus:outline-none" />
                  <button onClick={handleTip} disabled={tipAmount <= 0} className="h-9 px-5 bg-green-600 hover:bg-green-500 text-white rounded-md text-xs font-bold uppercase disabled:opacity-30 transition">Drop Commissary</button>
                </div>
              </div>
            )}

            {/* DESCRIPTION */}
            {data.description && (
              <div className="py-4 border-b border-neutral-800/40">
                <p className="text-sm text-neutral-300 whitespace-pre-wrap">{data.description}</p>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {tags.map((tag: string) => <Link key={tag} href={`/browse?q=${tag}`} className="px-2 py-0.5 rounded bg-neutral-900 border border-neutral-800 text-[10px] font-bold text-neutral-400 hover:text-white transition">{tag}</Link>)}
                  </div>
                )}
              </div>
            )}

            {/* RELATED */}
            {related && related.length > 0 && (
              <div className="py-6">
                <h3 className="text-sm font-bold uppercase tracking-widest text-neutral-400 mb-4">More from {creator.username}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {related.map((item: any) => <ContentCard key={item.id} content={item} variant="compact" />)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* KITES */}
        <div className="xl:w-[360px] xl:border-l border-neutral-800/40 shrink-0 h-[500px] xl:h-[calc(100vh-52px)] xl:sticky xl:top-[52px]">
          <ContentChat contentId={contentId} />
        </div>
      </div>
    </main>
  );
}
